import {
  getCurrencyConfigFromObject as getCurrencyConfigFromObjectConfig,
  formatPriceWithCurrencyObject,
} from './currency-config'

// Interface for currency object from backend
export interface CurrencyObject {
  code?: string
  symbol?: string
  name?: string
  locale?: string
  decimalPlaces?: number
  position?: 'before' | 'after'
  minRunningBet?: number
  betSetPrice?: number[]
}

/**
 * Format bet price with currency object from backend
 * @param price - Bet price value
 * @param currencyObject - Currency object from backend
 * @param hideSymbol - Whether to hide the symbol
 * @param spaceBetweenCurrencyAndNumber - Whether to space between currency and number
 * @returns Formatted price string
 */
export const formatBetPriceWithCurrencyObject = (
  price: number,
  currencyObject?: CurrencyObject,
  hideSymbol?: boolean,
  spaceBetweenCurrencyAndNumber?: boolean,
): string => {
  // Convert CurrencyObject to CurrencyConfig
  const currencyConfig = currencyObject
    ? {
        code: currencyObject.code || 'THB',
        symbol: currencyObject.symbol || '฿',
        name: currencyObject.name || 'Thai Baht',
        locale: currencyObject.locale || 'th-TH',
        decimalPlaces: currencyObject.decimalPlaces || 0,
        position: currencyObject.position || 'before',
      }
    : undefined
  return formatPriceWithCurrencyObject(price, currencyConfig, {
    showSymbol: !hideSymbol,
    decimalPlaces: currencyObject?.decimalPlaces || 0,
    spaceBetweenCurrencyAndNumber: spaceBetweenCurrencyAndNumber || false,
  })
}

/**
 * Get currency display from currency object
 * @param currencyObject - Currency object from backend
 * @returns Currency symbol or code for display
 */
export const getCurrencyDisplayFromObject = (
  currencyObject?: CurrencyObject,
): string => {
  if (!currencyObject) {
    return '฿' // Default fallback
  }

  // Convert CurrencyObject to CurrencyConfig
  const currencyConfig = {
    code: currencyObject.code || 'THB',
    symbol: currencyObject.symbol || '฿',
    name: currencyObject.name || 'Thai Baht',
    locale: currencyObject.locale || 'th-TH',
    decimalPlaces: currencyObject.decimalPlaces || 0,
    position: currencyObject.position || 'before',
  }

  const config = getCurrencyConfigFromObjectConfig(currencyConfig)
  return config.symbol
}

/**
 * Get bet prices from currency object or fallback
 * @param currencyObject - Currency object from backend
 * @param fallbackPrices - Fallback bet prices
 * @returns Array of bet prices
 */
export const getBetPricesFromCurrencyObject = (
  currencyObject?: CurrencyObject,
  fallbackPrices?: number[],
): number[] => {
  if (
    currencyObject?.betSetPrice &&
    Array.isArray(currencyObject.betSetPrice)
  ) {
    return currencyObject.betSetPrice
  }

  return fallbackPrices || [10, 20, 50, 100]
}

/**
 * Get min running bet from currency object or fallback
 * @param currencyObject - Currency object from backend
 * @param fallbackValue - Fallback min running bet value
 * @returns Min running bet value
 */
export const getMinRunningBetFromCurrencyObject = (
  currencyObject?: CurrencyObject,
  fallbackValue?: number,
): number => {
  if (
    currencyObject?.minRunningBet &&
    typeof currencyObject.minRunningBet === 'number'
  ) {
    return currencyObject.minRunningBet
  }

  return fallbackValue || 30
}

/**
 * Get currency configuration from currency object
 * @param currencyObject - Currency object from backend
 * @returns Currency configuration
 */
export const getCurrencyConfigFromObject = (
  currencyObject?: CurrencyObject,
) => {
  if (!currencyObject) {
    return {
      code: 'THB',
      symbol: '฿',
      name: 'Thai Baht',
      locale: 'th-TH',
      decimalPlaces: 2,
      position: 'before',
    }
  }

  return {
    code: currencyObject.code || 'THB',
    symbol: currencyObject.symbol || '฿',
    name: currencyObject.name || 'Thai Baht',
    locale: currencyObject.locale || 'th-TH',
    decimalPlaces: currencyObject.decimalPlaces || 0,
    position: currencyObject.position || 'before',
  }
}
